import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect } from 'react';
import { parseDurationToSeconds } from '@portfolioiq/shared';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
export function LogWorkModal({ issueKey, issueSummary, projectId, onClose, onSuccess, canLogOnBehalf, editWorklog, }) {
    const { showToast } = useToast();
    const { user } = useAuth();
    // Form state
    const [timeSpent, setTimeSpent] = useState(editWorklog ? formatHoursToInput(editWorklog.timeSpentHours) : '');
    const [date, setDate] = useState(editWorklog?.startDate || new Date().toISOString().slice(0, 10));
    const [description, setDescription] = useState(editWorklog?.description || '');
    const [selectedAuthor, setSelectedAuthor] = useState('');
    const [timeError, setTimeError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // Team members for "on behalf of"
    const [teamMembers, setTeamMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    // Fetch team members if canLogOnBehalf
    useEffect(() => {
        if (!canLogOnBehalf || !projectId)
            return;
        const controller = new AbortController();
        const fetchMembers = async () => {
            setLoadingMembers(true);
            try {
                const resp = await fetch(`/api/worklogs/team-members/${projectId}`, {
                    credentials: 'include',
                    signal: controller.signal,
                });
                if (resp.ok) {
                    const data = await resp.json();
                    setTeamMembers(data.members || []);
                }
            }
            catch (err) {
                if (err instanceof DOMException && err.name === 'AbortError')
                    return;
            }
            finally {
                if (!controller.signal.aborted)
                    setLoadingMembers(false);
            }
        };
        void fetchMembers();
        return () => controller.abort();
    }, [canLogOnBehalf, projectId]);
    // Close on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);
    const handleTimeChange = (val) => {
        setTimeSpent(val);
        if (val.trim() && parseDurationToSeconds(val) === null) {
            setTimeError('Invalid format. Use e.g. 2h 30m, 1d, 1h');
        }
        else {
            setTimeError('');
        }
    };
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!timeSpent.trim())
            return;
        const seconds = parseDurationToSeconds(timeSpent);
        if (seconds === null || seconds <= 0) {
            setTimeError('Invalid duration');
            return;
        }
        const timeSpentHours = Math.round((seconds / 3600) * 100) / 100;
        setSubmitting(true);
        try {
            if (editWorklog) {
                // Update existing worklog
                const resp = await fetch(`/api/worklogs/${editWorklog.tempoWorklogId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        timeSpentHours,
                        startDate: date,
                        description: description.trim() || undefined,
                    }),
                });
                if (!resp.ok) {
                    const data = await resp.json().catch(() => ({ error: 'Failed to update worklog' }));
                    throw new Error(data.error || 'Failed to update worklog');
                }
                showToast('Worklog update submitted for approval', 'success');
            }
            else {
                // Create new worklog
                const body = {
                    issueKey,
                    timeSpentHours,
                    startDate: date,
                    description: description.trim() || undefined,
                };
                // If logging on behalf of someone else, include authorAccountId
                if (selectedAuthor) {
                    body.authorAccountId = selectedAuthor;
                }
                const resp = await fetch('/api/worklogs/log', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(body),
                });
                if (!resp.ok) {
                    const data = await resp.json().catch(() => ({ error: 'Failed to create worklog' }));
                    throw new Error(data.error || 'Failed to create worklog');
                }
                showToast('Worklog submitted for approval', 'success');
            }
            onSuccess();
            onClose();
        }
        catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to submit worklog', 'error');
        }
        finally {
            setSubmitting(false);
        }
    }, [issueKey, timeSpent, date, description, selectedAuthor, editWorklog, onClose, onSuccess, showToast]);
    return (_jsxs("div", { className: "fixed inset-0 z-50 flex items-center justify-center", children: [_jsx("div", { className: "absolute inset-0 bg-black/30", onClick: onClose }), _jsxs("div", { className: "relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto", children: [_jsxs("div", { className: "px-6 py-4 border-b border-border", children: [_jsx("h3", { className: "text-lg font-semibold text-text-primary", children: editWorklog ? 'Edit Work Log' : 'Log Work' }), _jsxs("p", { className: "text-sm text-text-secondary mt-0.5", children: [_jsx("span", { className: "font-mono", children: issueKey }), _jsx("span", { className: "mx-1.5 text-text-tertiary", children: "\u2014" }), _jsx("span", { className: "truncate", children: issueSummary })] })] }), _jsxs("form", { onSubmit: handleSubmit, children: [_jsxs("div", { className: "px-6 py-4 space-y-4", children: [_jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-text-primary mb-1", children: ["Time Spent ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx("input", { type: "text", value: timeSpent, onChange: (e) => handleTimeChange(e.target.value), placeholder: "e.g. 2h 30m, 1d, 4h", className: `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${timeError ? 'border-red-300 focus:ring-red-500' : 'border-border'}`, disabled: submitting, autoFocus: true }), timeError && (_jsx("p", { className: "text-xs text-red-600 mt-1", children: timeError })), _jsx("p", { className: "text-xs text-text-tertiary mt-1", children: "Format: weeks (w), days (d), hours (h), minutes (m). 1w = 5d, 1d = 8h" })] }), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-text-primary mb-1", children: ["Date ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx("input", { type: "date", value: date, onChange: (e) => setDate(e.target.value), className: "w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500", disabled: submitting })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-text-primary mb-1", children: "Description" }), _jsx("textarea", { value: description, onChange: (e) => setDescription(e.target.value), placeholder: "What did you work on?", rows: 3, className: "w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none", disabled: submitting })] }), canLogOnBehalf && !editWorklog && (_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-text-primary mb-1", children: "Log Work For" }), _jsxs("select", { value: selectedAuthor, onChange: (e) => setSelectedAuthor(e.target.value), className: "w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500", disabled: submitting || loadingMembers, children: [_jsxs("option", { value: "", children: ["Myself (", user?.displayName || 'Current User', ")"] }), teamMembers.map((member) => (_jsx("option", { value: member.accountId, children: member.name }, member.accountId)))] }), loadingMembers && (_jsx("p", { className: "text-xs text-text-tertiary mt-1", children: "Loading team members..." }))] }))] }), _jsxs("div", { className: "px-6 py-4 border-t border-border space-y-3", children: [_jsxs("p", { className: "text-xs text-text-tertiary", children: ["This will create a write-back request that must be approved before the worklog is ", editWorklog ? 'updated in' : 'created in', " Tempo."] }), _jsxs("div", { className: "flex justify-end gap-3", children: [_jsx("button", { type: "button", onClick: onClose, className: "px-4 py-2 text-sm text-text-secondary hover:text-text-primary border border-border rounded-lg", disabled: submitting, children: "Cancel" }), _jsx("button", { type: "submit", disabled: !timeSpent.trim() || !!timeError || submitting, className: "px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed", children: submitting ? 'Submitting...' : 'Submit for Approval' })] })] })] })] })] }));
}
/** Convert decimal hours to a human-readable input string */
function formatHoursToInput(hours) {
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    const parts = [];
    if (h > 0)
        parts.push(`${h}h`);
    if (m > 0)
        parts.push(`${m}m`);
    return parts.join(' ') || '0m';
}
//# sourceMappingURL=LogWorkModal.js.map